import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import CertificationsService from '@/services/CertificationsService'
import TrainersService from '@/services/TrainersService'
import TrainingsService from '@/services/TrainingsService'

const formatDate = value => value ? `${value}`.substring(0, 10) : '—'

export default function useMyTrainingDetailsPage() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const [training, setTraining] = useState(null)
	const [trainers, setTrainers] = useState([])
	const [certificationOptions, setCertificationOptions] = useState([])
	const [loading, setLoading] = useState(true)

	const loadTraining = useCallback(async () => {
		setLoading(true)
		const [trainingResult, trainersResult, certificationsResult] = await Promise.all([
			TrainingsService.getById(id),
			TrainersService.getAll({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
			CertificationsService.getAll({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
		])

		if (trainingResult.error || !trainingResult.data) {
			await Swal.fire(
				t('notifications.errorTitle'),
				trainingResult.error || t('myTrainings.details.messages.loadFailed'),
				'error'
			)
			navigate('/centrumSzkolen/moje-szkolenia')
			return
		}

		if (trainersResult.error || certificationsResult.error) {
			await Swal.fire(
				t('notifications.errorTitle'),
				trainersResult.error || certificationsResult.error || t('myTrainings.details.messages.loadFailed'),
				'error'
			)
			navigate('/centrumSzkolen/moje-szkolenia')
			return
		}

		setTraining(trainingResult.data)
		setTrainers(Array.isArray(trainersResult.data) ? trainersResult.data : [])
		setCertificationOptions(
			(Array.isArray(certificationsResult.data) ? certificationsResult.data : []).map(item => ({
				value: `${item._id || item.id || ''}`.trim(),
				label: item.name || '—',
			})).filter(item => item.value)
		)
		setLoading(false)
	}, [id, navigate, t])

	useEffect(() => {
		if (id) {
			loadTraining()
		}
	}, [id, loadTraining])

	const refreshTraining = useCallback(async () => {
		const result = await TrainingsService.getById(id)
		if (!result.error && result.data) {
			setTraining(result.data)
		}
	}, [id])

	const statusLabel = useMemo(() => {
		if (!training?.status) return '—'
		return t(`trainings.list.statusOptions.${training.status}`, { defaultValue: training.status })
	}, [t, training?.status])

	const certificationMap = useMemo(
		() => new Map(
			(Array.isArray(certificationOptions) ? certificationOptions : []).map(item => [
				`${item.value}`,
				item.label || '—',
			])
		),
		[certificationOptions]
	)

	const certificationNames = useMemo(() => {
		const certificates = Array.isArray(training?.certificates) ? training.certificates : []
		const ids = Array.isArray(training?.requiredCertificationIds) && training.requiredCertificationIds.length
			? training.requiredCertificationIds
			: certificates.map(item => item.certificateId)

		return (Array.isArray(ids) ? ids : [])
			.map(id => {
				const key = `${id ?? ''}`.trim()
				if (!key) return ''
				const stored = certificates.find(item => `${item.certificateId}` === key)
				const storedName = `${stored?.certificateName || ''}`.trim()
				if (storedName) return storedName
				return certificationMap.get(key) || ''
			})
			.filter(Boolean)
	}, [certificationMap, training?.certificates, training?.requiredCertificationIds])

	const currentTrainer = useMemo(() => {
		const firstTrainer = Array.isArray(trainers) ? trainers[0] : null
		return firstTrainer || null
	}, [trainers])

	const currentTrainerName = currentTrainer?.name || ''

	const trainerRows = useMemo(() => {
		if (!training) return []

		const trainerNames = Array.isArray(training.trainerNames) ? training.trainerNames.filter(Boolean) : []
		const trainerCompensation = Array.isArray(training.trainerCompensation) ? training.trainerCompensation : []

		const rows = trainerNames.map(trainerName => {
			const trainer = (Array.isArray(trainers) ? trainers : []).find(item => item?.name === trainerName)
			const compensation = trainerCompensation.find(item => item?.trainerName === trainerName) || {}

			return {
				key: trainer?._id || trainer?.id || trainerName,
				trainerName,
				trainerCity: trainer?.city || '',
				hourlyRateGross: compensation.hourlyRateGross || trainer?.defaultHourlyRateGross || '',
				extraAmountGross: compensation.extraAmountGross || '',
			}
		})

		if (!currentTrainerName) {
			return rows
		}

		return [...rows].sort((left, right) => {
			const leftIsCurrent = `${left.trainerName || ''}`.trim().toLowerCase() === `${currentTrainerName}`.trim().toLowerCase()
			const rightIsCurrent = `${right.trainerName || ''}`.trim().toLowerCase() === `${currentTrainerName}`.trim().toLowerCase()

			if (leftIsCurrent === rightIsCurrent) return 0
			return leftIsCurrent ? -1 : 1
		})
	}, [currentTrainerName, training, trainers])

	const uploadTrainingFile = useCallback(async (file, onUploadProgress) => {
		const result = await TrainingsService.uploadTrainingFile(id, file, onUploadProgress)

		if (result.success) {
			await refreshTraining()
		}

		return result
	}, [id, refreshTraining])

	const downloadTrainingFile = useCallback(
		fileUuid => TrainingsService.downloadTrainingFile(id, fileUuid),
		[id]
	)

	const deleteTrainingFile = useCallback(async fileUuid => {
		const result = await TrainingsService.deleteTrainingFile(id, fileUuid)

		if (result.success) {
			await refreshTraining()
		}

		return result
	}, [id, refreshTraining])

	return {
		certificationNames,
		createdDate: formatDate(training?.created),
		currentTrainer,
		currentTrainerName,
		loading,
		statusLabel,
		trainerRows,
		training,
		trainingId: id,
		uploadTrainingFile,
		downloadTrainingFile,
		deleteTrainingFile,
	}
}
